import React, { useCallback, useState } from 'react'
import { ProjectTeaser } from '@betterplace/betterplace-react-components'
import { RecordList, useSearch } from 'shared'

import { CtaBoxProps, ProjectSearchProps, RenderSearchResult, SearchResult, SearchState } from './types'

import { Project } from 'shared/hooks/use_project/types'
import { ProjectModal } from '../project_modal'

export const ProjectSearch = ({ apiBaseUrl, projectSelectionUrl, ...search }: ProjectSearchProps): JSX.Element => {
  const searchResult: SearchState = useSearch({ apiBaseUrl, ...search })

  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [modalProject, setModalProject] = useState<SearchResult | undefined>(undefined)
  const openModal = useCallback((project) => {
    setModalProject(project)
    setShowPreviewModal(true)
    const ele = document.activeElement as HTMLElement | null
    ele?.blur()
  }, [])
  const hideModal = useCallback(() => {
    setShowPreviewModal(false)
  }, [])

  const handleProjectTeaserClick = useCallback(
    (event: React.MouseEvent, project: SearchResult) => {
      if (event.defaultPrevented) return

      event.preventDefault()
      openModal(project)
    },
    [openModal]
  )
  const renderTeaser: RenderSearchResult = useCallback(
    (project: SearchResult, _index) => {
      const selectionUrl = projectSelectionUrl.replace('PROJECT_ID', `${project.id}`)
      return (
        <ProjectTeaser
          project={project}
          openInTab={false}
          bottomContent={<CtaBox selectionUrl={selectionUrl} project={project} openModal={openModal} />}
          extraHeight={50}
          showCarrierName
          href="#"
          onClick={(event) => handleProjectTeaserClick(event, project)}
        />
      )
    },
    [handleProjectTeaserClick, projectSelectionUrl, openModal]
  )

  return (
    <>
      {searchResult.loading ? (
        <h2 className="text-center mb-3 mt-1 desktop:mt-3 text-lg desktop:text-2xl">
          {i18n.t('fundraising_events.preselections.show.loading_subheadline')}
        </h2>
      ) : (
        <div className="text-center">
          <h2 className="text-center mt-1 desktop:mt-3 mb-0 text-lg desktop:text-2xl">
            {i18n.t('fundraising_events.preselections.show.results_subheadline', {
              count: searchResult.totalEntries ?? 0,
            })}
          </h2>
          <span className="block mb-3 desktop:mb-6">
            {i18n.t('fundraising_events.preselections.show.results_explanation', {
              count: searchResult.totalEntries ?? 0,
            })}
          </span>
        </div>
      )}
      <RecordList {...searchResult} render={renderTeaser} />
      {modalProject && (
        <ProjectModal
          hideModal={hideModal}
          showPreviewModal={showPreviewModal}
          projectSelectionUrl={projectSelectionUrl}
          //We know that it is a project and not a fundraising event
          project={modalProject as unknown as Project}
        />
      )}
    </>
  )
}

const CtaBox = ({ selectionUrl, openModal, project }: CtaBoxProps): JSX.Element => {
  const handleMoreInfoClick: React.MouseEventHandler<HTMLSpanElement> = useCallback(
    (event) => {
      event.preventDefault()
      openModal(project)
    },
    [openModal, project]
  )
  const handleSelectClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault()
      window.open(selectionUrl, event.metaKey ? undefined : '_self')
    },
    [selectionUrl]
  )
  return (
    <>
      <div className="cta-box flex flex-col items-center justify-between">
        <button className="btn btn-primary w-full mb-2" onClick={handleSelectClick}>
          {i18n.t('fundraising_events.preselections.show.cta_button')}
        </button>
        <button className="simulated-link text-sm" onClick={handleMoreInfoClick}>
          {i18n.t('fundraising_events.preselections.show.cta_more')}
        </button>
      </div>
    </>
  )
}
